import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import Info from "../Components/Info";
import AskForm from "../Components/AskForm";
import HeightInterval from "../Components/HeightInterval.jsx";
import useStore from '../store.js';
import ChatContainer from './ChatContainer';
import ChatSearchHistory from './ChatSearchHistory.jsx';
import {
  observeChatResize,
  scheduleChatScrollToBottom,
} from '../utils/chatScroll.js';

const Ask = ({ initialTextValue, guestMode = false, onDraftChange, draftRef }) => {
  const {
    isChatMode,
    chatHistory,
    isAIResponding,
    isChatDone,
    chatErrorMessage,
    guestRequestUsed,
  } = useStore();
  const chatScrollAreaRef = useRef(null);
  const [textValue, setTextValue] = useState(initialTextValue || "");
  const chatInputRef = useRef(null);
  const [directInputRequested, setDirectInputRequested] = useState(false);

  useEffect(() => {
    setTextValue(initialTextValue || "");
  }, [initialTextValue]);

  useEffect(() => {
    setDirectInputRequested(false);
  }, [chatHistory, isChatMode]);

  useEffect(() => {
    if (!isChatMode || !chatScrollAreaRef.current) return undefined;

    return scheduleChatScrollToBottom({
      getScrollArea: () => chatScrollAreaRef.current,
      requestFrame: window.requestAnimationFrame.bind(window),
      cancelFrame: window.cancelAnimationFrame.bind(window),
    });
  }, [chatErrorMessage, chatHistory, isAIResponding, isChatDone, isChatMode]);

  useEffect(() => {
    if (!isChatMode) return undefined;

    return observeChatResize({
      scrollArea: chatScrollAreaRef.current,
      ResizeObserverClass: window.ResizeObserver,
    });
  }, [isChatMode]);

  const handleKeywordClick = (exampleText) => {
    setTextValue(exampleText);
  };

  const handleRequestDirectInput = () => {
    chatInputRef.current?.focus({ preventScroll: true });
    setDirectInputRequested(true);
  };

  if (isChatMode) {
    return (
      <ChatModeWrapper>
        <ChatScrollArea ref={chatScrollAreaRef} data-testid="chat-scroll-area">
          <ChatScrollInner data-testid="chat-scroll-inner">
            <ChatContainer guestMode={guestMode} onRequestDirectInput={handleRequestDirectInput} />
          </ChatScrollInner>
        </ChatScrollArea>
        <ChatSearchHistoryDock>
          <ChatSearchHistory />
        </ChatSearchHistoryDock>
        <ChatBottomBar>
          <AskForm
            initialTextValue={textValue}
            guestMode={guestMode}
            focusRef={chatInputRef}
            directInputRequested={directInputRequested}
            onDraftChange={onDraftChange}
            draftRef={draftRef}
          />
        </ChatBottomBar>
      </ChatModeWrapper>
    );
  }

  return (
    <Wrapper $guestMode={guestMode}>
      <LeftAlignedContainer>
        <Info onKeywordClick={handleKeywordClick} />
      </LeftAlignedContainer>
      <HeightInterval height={guestMode ? 24 : 48} />
      <AskForm initialTextValue={textValue} guestMode={guestMode} onDraftChange={onDraftChange} draftRef={draftRef} />
      {guestMode && !guestRequestUsed && (
        <GuestPolicyNotice>
          로그인 없이 대화 1회 무료
        </GuestPolicyNotice>
      )}
      {!guestMode && <Notice>
        *기재한 내용은 AI 학습 데이터로 수집 및 활용되지 않습니다.
      </Notice>}
    </Wrapper>
  );
};

const ChatModeWrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: calc(100vh - 83px);
  height: calc(100dvh - 83px);
  min-height: 0;
  width: 100%;
  overflow: hidden;
`;

const ChatScrollArea = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 31px 0 0;
  overscroll-behavior-y: contain;

  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(0, 0, 0, 0.08);
    border-radius: 3px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
`;

const ChatScrollInner = styled.div`
  min-height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  width: 100%;
  box-sizing: border-box;
`;

const ChatSearchHistoryDock = styled.div`
  flex-shrink: 0;
  display: flex;
  justify-content: center;
  width: 100%;
  overflow-x: hidden;
  padding: 8px 0 20px;
  background: #fff;
`;

const ChatBottomBar = styled.div`
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 12px 0 24px;
  background: #fff;
  border-top: 0.3px solid rgba(0, 0, 0, 0.08);
`;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  width: 100%;
  min-height: ${({ $guestMode }) => ($guestMode ? "0" : "calc(100vh - 83px)")};
  padding-top: ${({ $guestMode }) => ($guestMode ? "24px" : "100px")};
  box-sizing: border-box;
`;

const Notice = styled.div`
  color: rgba(0, 0, 0, 0.3);
  font-family: Pretendard;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
  padding-top: 20px;
`;

const GuestPolicyNotice = styled.div`
  padding-top: 16px;
  color: rgba(0, 0, 0, 0.58);
  font-family: Pretendard;
  font-size: 13px;
  font-weight: 600;
  line-height: 20px;
  letter-spacing: -0.26px;
  text-align: center;
`;

const LeftAlignedContainer = styled.div``;

export default Ask;
